import { c as _c } from "react/compiler-runtime";
import "client-only";
import { type RefObject, useActionState, useEffect, useId, useRef } from "react";
import { createComment } from "@/app/[domain]/(sites)/_actions/comments/create-comment";
import { useComments } from "@/components/Comment/context/comments.context";
import { useCommentsActions } from "@/components/Comment/hooks/use-comment-actions.hook";
import type { CommentModel } from "@/services/api/comment/models/comment.model.types";
import { captureClientException } from "@/services/logging/capture-client-exception";
import { type Fetchable, hasFailed, hasSucceeded, initial } from "@/services/utilities/fetchable";
type CommentFormInput = {
  // TODO: Remove when backend supports flatten on createComment
  readonly ancestorId?: string | null;
  readonly parent?: CommentModel.ParentReference | null;
  readonly onSuccess?: () => void;
  readonly onCancel?: () => void;
};
type CommentFormRefs = {
  readonly formRef: RefObject<HTMLFormElement | null>;
  readonly textareaRef: RefObject<HTMLTextAreaElement | null>;
};
type CommentFormState = {
  readonly state: Fetchable<CommentModel>;
  readonly isPending: boolean;
  readonly uniqueId: string;
};
type CommentFormActions = {
  readonly formAction: (payload: FormData) => void;
  readonly handleCancel: () => void;
};
type UseCommentFormApi = CommentFormRefs & CommentFormState & CommentFormActions;
export function useCommentForm(t0) {
  const $ = _c(20);
  const {
    parent,
    onSuccess,
    onCancel,
    ancestorId
  } = t0;
  const {
    storyId
  } = useComments();
  const {
    addComment,
    addReply
  } = useCommentsActions();
  const formRef = useRef(null);
  const textareaRef = useRef(null);
  const uniqueId = useId();
  let t1;
  if ($[0] !== parent || $[1] !== storyId) {
    t1 = createComment.bind(null, storyId, parent ?? null);
    $[0] = parent;
    $[1] = storyId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const boundAction = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = initial();
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const [state, formAction, isPending] = useActionState(boundAction, t2);
  const previousStateRef = useRef(state);
  let t3;
  let t4;
  if ($[4] !== addComment || $[5] !== addReply || $[6] !== ancestorId || $[7] !== onSuccess || $[8] !== parent || $[9] !== state || $[10] !== storyId) {
    t3 = () => {
      if (state === previousStateRef.current) {
        return;
      }
      previousStateRef.current = state;
      if (hasFailed(state)) {
        captureClientException(new Error(parent ? "Failed to create reply" : "Failed to create comment"), {
          extra: {
            parentId: parent?.id,
            storyId
          }
        });
        return;
      }
      if (!hasSucceeded(state) || state.value === undefined) {
        return;
      }
      const comment = state.value;
      if (parent) {
        addReply(comment, ancestorId);
      } else {
        addComment(comment);
      }
      formRef.current?.reset();
      textareaRef.current?.blur();
      onSuccess?.();
    };
    t4 = [state, parent, addComment, addReply, onSuccess, ancestorId, storyId];
    $[4] = addComment;
    $[5] = addReply;
    $[6] = ancestorId;
    $[7] = onSuccess;
    $[8] = parent;
    $[9] = state;
    $[10] = storyId;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== onCancel) {
    t5 = () => {
      textareaRef.current?.blur();
      onCancel?.();
    };
    $[13] = onCancel;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const handleCancel = t5;
  let t6;
  if ($[15] !== handleCancel || $[16] !== isPending || $[17] !== state || $[18] !== uniqueId) {
    t6 = {
      formAction,
      formRef,
      handleCancel,
      isPending,
      state,
      textareaRef,
      uniqueId
    };
    $[15] = handleCancel;
    $[16] = isPending;
    $[17] = state;
    $[18] = uniqueId;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}